import { AlgoliaSearch } from "@/services/algoliaSearch"
import { CommerceService } from "@/services/commerce"
import { AppSettings } from "@/settings"

import { isOnlyIdmAction } from "../../shared/permissions/permissions"
import { getBranchOfficeById, getBranchOfficesTitle } from "../../shared/utils/getBranchOffice"
import {
  getExcludedCustomerGroups,
  getExcludedIndustryKeys,
  getFallbackExcludedIndustryKeys,
  noDataReturn,
} from "../utils/customerQueryHelpers"

const commerceApi = CommerceService.api()

export const queryIdmCustomers = {
  async idmCustomers(_parent, args, context, _info) {
    const {
      offset,
      limit,
      branchId,
      page,
      searchTerm,
      lang = AppSettings.defaultLocale(),
      cdn = true,
      internalSearch,
    } = args
    const { user } = context
    isOnlyIdmAction(user)

    if (internalSearch) {
      return await searchAlgoliaCustomers({ page, searchTerm, limit, internalSearch })
    }

    if (searchTerm && searchTerm.length > 0) {
      try {
        let ctCustomer = null
        if (Number(page) < 2 && searchTerm?.length > 0 && /^\d{5}$/.test(searchTerm)) {
          ctCustomer = await getCtCustomer({
            customerNumber: searchTerm,
            branchId,
            lang,
            context,
            cdn,
          })
        }

        const algoliaCustomers = await searchAlgoliaCustomers({ page, searchTerm, branchId, limit })

        return mergeCustomers({ algoliaCustomers, ctCustomer })
      } catch (err) {
        console.warn("Error searching customers in algolia", err)
        return await getCtCustomers({ offset, limit, branchId, context, lang, cdn })
      }
    }

    return await getCtCustomers({ offset, limit, branchId, context, lang, cdn })
  },
}

/**
 * Returns an object with mapped customers ready for return
 *
 * @param {{offset?: number, limit?: number, branchId?: string, context: object, lang: string, cdn: boolean}}
 *
 * @returns {Promise<{items: Array, limit: number, offset: number, count: number, total: number}>}
 * @throws {Error}
 */
const getCtCustomers = async ({ offset, limit, branchId, context, lang, cdn }) => {
  const excludedCustomerGroups = await getExcludedCustomerGroups()
  const excludedIndustryKeys = await getExcludedIndustryKeys()
  const branchOffices = await getBranchOfficesTitle(lang, context, cdn)

  const branchPredicate = getBranchPredicate(branchId)

  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          limit: limit,
          offset: offset,
          sort: "custom.fields.revenueYTD desc",
          where: `customerGroup(id not in (${excludedCustomerGroups})) and custom(fields(industryKey(id is defined and id not in (${excludedIndustryKeys}))))${branchPredicate}`,
        },
      })
      .execute()
  )?.body

  if (!customers || customers?.results?.length === 0) {
    return noDataReturn(0, limit)
  }

  return {
    items: mapCustomerData(customers?.results, branchOffices),
    limit,
    offset,
    count: customers?.count,
    total: customers?.total,
  }
}

/**
 * Returns mapped customer data
 *
 * @param {array} customers
 *
 * @returns {Array}
 */
const mapCustomerData = (customers, branchOffices) => {
  return customers.map((customer) => {
    const customerBillingAddress = customer?.addresses?.find(
      (address) => address?.id === customer?.defaultBillingAddressId,
    )

    const branchId = customer?.custom?.fields?.deliveryBranch || customer?.custom?.fields?.customerBranch

    const branchTitle = branchOffices?.find((branch) => branch?.id === branchId)?.title || ""

    return {
      id: customer.id,
      customerNumber: customer.customerNumber,
      companyName: customer.companyName,
      matchCode: customer?.custom?.fields?.matchCode,
      customerBranch: customer?.custom?.fields?.customerBranch,
      admRepresentative: customer?.custom?.fields?.admSalesRepresentive,
      city: customerBillingAddress?.city,
      streetName: customerBillingAddress?.streetName,
      postalCode: customerBillingAddress?.postalCode,
      customerClassification: customer?.custom?.fields?.customerClassification,
      branchId,
      branchTitle,
    }
  })
}

/**
 * Retrieves customers from algolia
 *
 * @param {{ page?: number, branchId?: string, searchTerm: string, limit?: number, internalSearch?: boolean }}
 * @returns {Promise<{items: Array, limit: number, count: number, total: number, page: number}>}
 * @throws {Error}
 */
const searchAlgoliaCustomers = async ({ page, searchTerm, branchId, limit, internalSearch }) => {
  const algoliaClient = AlgoliaSearch.client()

  const hasCustomers = await algoliaClient.searchSingleIndex({
    indexName: internalSearch ? "internal_customers" : "customers",
    searchParams: { query: "" },
  })

  if (!hasCustomers) throw new Error("No customers found in algolia")

  const usedPage = page ? Number(page) - 1 : 0
  const customersResponse = await algoliaClient.searchSingleIndex({
    indexName: internalSearch ? "internal_customers" : "customers",
    searchParams: {
      query: searchTerm ? searchTerm : "",
      page: usedPage,
      clickAnalytics: true,
      hitsPerPage: limit,
      ...(branchId?.length > 0 && {
        filters: `customerBranch:${branchId}`,
      }),
    },
  })

  if (!customersResponse || customersResponse?.hits?.length === 0) {
    return noDataReturn(page, limit)
  }

  if (process.env.NEXT_PUBLIC_TARGET_ENV !== "production") {
    const hitIds = customersResponse?.hits?.map((customer) => `"${customer?.customerNumber}"`)
    const ctCustomers = await getNonProdCustomerIds(hitIds)

    return {
      items: customersResponse?.hits?.map((customer) => {
        const customerId = ctCustomers.find((ctCustomer) => ctCustomer?.customerNumber === customer?.customerNumber)

        return algoliaCustomerMapper(customerId?.id || customer?.objectID, customer)
      }),
      limit,
      count: customersResponse?.hits?.length,
      total: customersResponse?.nbHits,
    }
  }

  return {
    items: customersResponse?.hits?.map((customer) => {
      return algoliaCustomerMapper(customer?.objectID, customer)
    }),
    limit,
    count: customersResponse?.hits?.length,
    total: customersResponse?.nbHits,
  }
}

/**
 * On dev and staging should be triggered to return customer ids for env
 * Algolia import is based on prod customers which have different ids for dev and staging
 *
 * @param {array<string>} customerIds
 *
 * @returns {Promise<array<{id: string, customerNumber: string}>>}
 */
const getNonProdCustomerIds = async (customerIds) => {
  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          where: `customerNumber in (${customerIds})`,
          limit: customerIds.length,
        },
      })
      .execute()
  )?.body?.results

  if (!customers || customers?.length === 0) {
    return []
  }

  return customers.map((customer) => {
    return { id: customer?.id, customerNumber: customer?.customerNumber }
  })
}

/**
 *
 * @param {string} id
 * @param {{
 * city: ?string,
 * companyName: ?string,
 * customerBranch: ?string,
 * customerNumber: ?string,
 * streetName: ?string,
 * postalCode: ?string,
 * matchCode: ?string,
 * customerClassification: ?string,
 * branchId: ?string,
 * branchTitle: ?string
 * }} customer
 * @returns {customer}
 */
const algoliaCustomerMapper = (id, customer) => {
  return {
    id,
    city: customer?.city,
    companyName: customer?.companyName,
    customerBranch: customer?.customerBranch,
    customerNumber: customer?.customerNumber,
    streetName: customer?.streetName,
    postalCode: customer?.postalCode,
    matchCode: customer?.matchCode,
    customerClassification: customer?.customerClassification,
    branchId: customer?.customerBranch, // in agolia: if delivery branch is not set, the customer branch is used
    branchTitle: customer?.branchTitle,
    admRepresentative: customer?.admSalesRepresentive,
  }
}

/**
 * Retrieves a customer by customer number from CommerceTools
 *
 * @param {{customerNumber: string, context: object, lang: string, cdn: boolean, branchId?: string}}
 * @returns {Promise<object|null>} Returns the customer object or null if not found
 */
const getCtCustomer = async ({ customerNumber, lang, context, cdn, branchId }) => {
  const [excludedCustomerGroups, excludedIndustryKeys] = await Promise.all([
    getExcludedCustomerGroups(),
    getFallbackExcludedIndustryKeys(),
  ])
  const branchPredicate = getBranchPredicate(branchId)

  const customers = (
    await commerceApi
      .customers()
      .get({
        queryArgs: {
          expand: "custom.fields.industryKey",
          where: `customerNumber = "${customerNumber}" and customerGroup(id not in (${excludedCustomerGroups})) and custom(fields(industryKey(id is defined and id not in (${excludedIndustryKeys}))))${branchPredicate}`,
          limit: 1,
        },
      })
      .execute()
  )?.body?.results

  if (!customers || customers?.length === 0) {
    return null
  }

  const customer = customers[0]

  const customerBranchId = customer?.custom?.fields?.deliveryBranch || customer?.custom?.fields?.customerBranch
  const branchOffice = await getBranchOfficeById(lang, context, cdn, customerBranchId)

  const customerBillingAddress = customer?.addresses?.find(
    (address) => address?.id === customer?.defaultBillingAddressId,
  )

  return {
    id: customer.id,
    customerNumber: customer.customerNumber,
    companyName: customer.companyName,
    matchCode: customer?.custom?.fields?.matchCode,
    customerBranch: customer?.custom?.fields?.customerBranch,
    admRepresentative: customer?.custom?.fields?.admSalesRepresentive,
    city: customerBillingAddress?.city,
    streetName: customerBillingAddress?.streetName,
    postalCode: customerBillingAddress?.postalCode,
    customerClassification: customer?.custom?.fields?.customerClassification,
    branchId: customerBranchId,
    branchTitle: branchOffice?.title || "",
  }
}

/**
 * Merges Algolia customers with a CommerceTools customer
 *
 * @param {{ algoliaCustomers: object, ctCustomer: object|null }}
 * @returns {object} Merged customers object
 */
const mergeCustomers = ({ algoliaCustomers, ctCustomer }) => {
  if (!ctCustomer) {
    return algoliaCustomers
  }

  if (algoliaCustomers.items.length === 0) {
    return {
      items: [ctCustomer],
      limit: 1,
      count: 1,
      total: 1,
      page: 0,
    }
  }

  const filteredArray = algoliaCustomers.items.filter(
    (customer) => customer.customerNumber !== ctCustomer.customerNumber,
  )

  const total =
    filteredArray.length === algoliaCustomers?.items?.length ? algoliaCustomers.total + 1 : algoliaCustomers.total
  const items = [ctCustomer, ...filteredArray]

  return {
    items,
    limit: algoliaCustomers.limit,
    count: items.length,
    total,
    page: algoliaCustomers.page,
  }
}

/**
 * @param {string} branchId
 * @returns {string}
 */
const getBranchPredicate = (branchId) => {
  return branchId?.length > 0
    ? `and (custom(fields(deliveryBranch is not empty and deliveryBranch = "${branchId}")) or custom(fields(deliveryBranch is empty and customerBranch = "${branchId}")))`
    : ""
}
